import {
  getLibraTicketGroupAdminPermission,
  type RolodexPermissionsValues,
} from 'types'
import z from 'zod'

export enum LifecycleTriggerType {
  FIRST_INTERACTION = 'first_interaction',
  STEP_REACHED = 'step_reached',
  WORKFLOW_COMPLETE = 'workflow_complete',
  ACTION = 'action',
}

const firstInteractionTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.FIRST_INTERACTION),
})

const stepReachedTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.STEP_REACHED),
  stepId: z.string(),
})

const workflowCompleteTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.WORKFLOW_COMPLETE),
  label: z.string().optional(),
})

const actionTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.ACTION),
  label: z.string(),
  roles: z.array(z.string()),
  component: z.string().optional(),
})

const lifecycleTriggerSchema = z.discriminatedUnion('type', [
  firstInteractionTriggerSchema,
  stepReachedTriggerSchema,
  workflowCompleteTriggerSchema,
  actionTriggerSchema,
])

// ---------------------------------------------------------------------------
// Stage & transition schemas
// ---------------------------------------------------------------------------

const lifecycleStageSchema = z.object({
  id: z.string(),
  name: z.string(),
  default: z.boolean().optional(),
  locked: z.boolean().optional(),
  terminal: z.boolean().optional(),
  color: z.string().optional(),
})

const lifecycleTransitionSchema = z.object({
  from: z.string(),
  to: z.string(),
  trigger: lifecycleTriggerSchema,
})

export const lifecycleConfigSchema = z.object({
  stages: z.array(lifecycleStageSchema),
  transitions: z.array(lifecycleTransitionSchema),
})

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type LifecycleConfig = z.infer<typeof lifecycleConfigSchema>
export type LifecycleStage = z.infer<typeof lifecycleStageSchema>
export type LifecycleTransition = z.infer<typeof lifecycleTransitionSchema>
export type LifecycleTrigger = z.infer<typeof lifecycleTriggerSchema>

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Returns the single automatic transition matching the given trigger type from
 * the current stage, or null if none exists.
 */
export function findMatchingTransition(
  config: LifecycleConfig,
  currentStageId: string,
  trigger:
    | { type: LifecycleTriggerType.FIRST_INTERACTION }
    | { type: LifecycleTriggerType.STEP_REACHED; stepId: string }
    | { type: LifecycleTriggerType.WORKFLOW_COMPLETE }
): LifecycleTransition | null {
  for (const t of config.transitions) {
    if (t.from !== currentStageId) continue
    if (t.trigger.type !== trigger.type) continue
    if (
      trigger.type === LifecycleTriggerType.STEP_REACHED &&
      t.trigger.type === LifecycleTriggerType.STEP_REACHED &&
      t.trigger.stepId !== trigger.stepId
    ) {
      continue
    }
    return t
  }
  return null
}

/**
 * Returns all action-type transitions available to a user from the current
 * stage. The `roles` field on each action trigger contains explicit rolodex
 * permission strings; they are passed directly to `hasPermissions`.
 * Users with the scoped admin permission for the given ticket group implicitly
 * match every action.
 */
export function getAvailableActions(
  config: LifecycleConfig,
  currentStageId: string,
  hasPermissions: (perms: RolodexPermissionsValues[]) => boolean,
  groupId: string | undefined
): ReadonlyArray<LifecycleTransition> {
  return config.transitions.filter((t) => {
    if (t.from !== currentStageId) return false
    if (t.trigger.type !== LifecycleTriggerType.ACTION) return false
    if (
      groupId &&
      hasPermissions([getLibraTicketGroupAdminPermission(groupId)])
    )
      return true

    if (t.trigger.roles.length === 0) return true

    return hasPermissions(t.trigger.roles as RolodexPermissionsValues[])
  })
}

/**
 * Returns the default stage (the one tickets are created in), falling back
 * to the first stage if none is explicitly marked.
 */
export function getDefaultStage(
  config: LifecycleConfig
): LifecycleStage | undefined {
  return config.stages.find((s) => s.default) ?? config.stages[0] ?? undefined
}

/**
 * Safely parses `globalContext.lifecycleConfig`. Returns the config if valid,
 * or undefined if absent/invalid. Logs validation errors to the console.
 */
export function parseLifecycleConfig(
  globalContext: Record<string, unknown>
): LifecycleConfig | undefined {
  const raw = globalContext.lifecycleConfig
  if (!raw || typeof raw !== 'object') return undefined
  const result = lifecycleConfigSchema.safeParse(raw)
  if (!result.success) {
    console.warn(
      '[Libra] lifecycleConfig failed validation:',
      result.error.issues
    )
  }
  return result.success ? result.data : undefined
}

/**
 * Returns the button label for the workflow-complete action. Checks the
 * current stage first, then falls back to the first `workflow_complete`
 * transition in the config that has a label. This ensures the correct
 * label shows even before the ticket has transitioned out of its default
 * stage (e.g. "Not Started" → "In Progress").
 */
export function getWorkflowCompleteLabel(
  config: LifecycleConfig | undefined,
  currentStageId: string
): string {
  if (!config) return 'Complete'

  const fromCurrent = findMatchingTransition(config, currentStageId, {
    type: LifecycleTriggerType.WORKFLOW_COMPLETE,
  })
  if (
    fromCurrent?.trigger.type === LifecycleTriggerType.WORKFLOW_COMPLETE &&
    fromCurrent.trigger.label
  ) {
    return fromCurrent.trigger.label
  }

  for (const t of config.transitions) {
    if (
      t.trigger.type === LifecycleTriggerType.WORKFLOW_COMPLETE &&
      t.trigger.label
    ) {
      return t.trigger.label
    }
  }

  return 'Complete'
}
